import { useRef, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { MdDelete } from "react-icons/md";

/* ─── animations ─────────────────────────────────────── */
const slideIn = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to   { opacity: 0; transform: translateX(60px); }
`;

/* ─── styled pieces ───────────────────────────────────── */
const ListWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  height: 100%;
  overflow-y: auto;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 180px;
  color: var(--grey-400, #aaa);
  gap: 0.5rem;
  font-size: 0.9rem;
  border: 2px dashed var(--grey-200, #e2e8f0);
  border-radius: var(--borderRadius, 6px);
  padding: 2rem;
  text-align: center;
`;

const ItemWrap = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  flex-shrink: 0;
  ${({ removing }) =>
    removing
      ? css`animation: ${fadeOut} 0.28s ease forwards;`
      : css`animation: ${slideIn} 0.25s ease both;`}
`;

const Track = styled.div`
  display: flex;
  align-items: center;
  transition: transform 0.22s ease;
  transform: ${({ revealed }) => (revealed ? "translateX(-68px)" : "translateX(0)")};
`;

const Card = styled.div`
  flex: 1;
  min-width: 0;
  background: var(--grey-50, #f8fafc);
  border: 1px solid var(--grey-200, #e2e8f0);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  cursor: grab;
  user-select: none;

  &:active { cursor: grabbing; }

  .para-index {
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--primary-500, #2563eb);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.2rem;
  }
  .para-preview {
    font-size: 0.82rem;
    color: var(--grey-700, #374151);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  .para-chars {
    font-size: 0.7rem;
    color: var(--grey-400, #9ca3af);
    margin-top: 0.25rem;
  }
`;

const DeleteReveal = styled.button`
  flex-shrink: 0;
  width: 60px;
  background: var(--red-dark, #dc2626);
  border: none;
  border-radius: 0 8px 8px 0;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  transition: background 0.15s;
  &:hover { background: #b91c1c; }
`;

const DragHandle = styled.div`
  position: absolute;
  top: 50%;
  right: ${({ revealed }) => (revealed ? "76px" : "8px")};
  transform: translateY(-50%);
  transition: right 0.22s ease;
  color: var(--grey-300, #d1d5db);
  font-size: 0.9rem;
  pointer-events: none;
  line-height: 1;
  letter-spacing: 1px;
`;

/* ─── single list item ────────────────────────────────── */
const ParagraphItem = ({ item, index, total, onDelete, onMove }) => {
  const [revealed, setRevealed] = useState(false);
  const [removing, setRemoving] = useState(false);
  const touchStartX = useRef(null);
  const dragStartIndex = useRef(null);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (delta > 40) setRevealed(true);
    else if (delta < -20) setRevealed(false);
    touchStartX.current = null;
  };

  const handleMouseDown = (e) => {
    const startX = e.clientX;
    const onUp = (upE) => {
      const delta = startX - upE.clientX;
      if (delta > 40) setRevealed(true);
      else if (delta < -20) setRevealed(false);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mouseup", onUp);
  };

  /* drag-to-reorder */
  const handleDragStart = (e) => {
    dragStartIndex.current = index;
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (dragStartIndex.current !== null && dragStartIndex.current !== index) {
      onMove(dragStartIndex.current, index);
      dragStartIndex.current = null;
    }
  };

  const handleDelete = () => {
    setRemoving(true);
    setTimeout(() => onDelete(index), 270);
  };

  const preview =
    item.paragraph.length > 80
      ? item.paragraph.substring(0, 80) + "…"
      : item.paragraph;

  return (
    <ItemWrap
      removing={removing}
      draggable
      onDragStart={handleDragStart}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <Track
        revealed={revealed}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
      >
        <Card>
          <div className="para-index">Paragraph {index + 1} of {total}</div>
          <div className="para-preview">{preview}</div>
          <div className="para-chars">{item.paragraph.length} chars</div>
        </Card>
        <DeleteReveal onClick={handleDelete} title="Delete paragraph">
          <MdDelete />
        </DeleteReveal>
      </Track>
      <DragHandle revealed={revealed}>⠿</DragHandle>
    </ItemWrap>
  );
};

/* ─── exported list ───────────────────────────────────── */
const ParagraphList = ({ paragraphs, onDelete, onReorder }) => {
  const handleMove = (fromIdx, toIdx) => {
    const updated = [...paragraphs];
    const [moved] = updated.splice(fromIdx, 1);
    updated.splice(toIdx, 0, moved);
    onReorder(updated);
  };

  if (paragraphs.length === 0) {
    return (
      <EmptyState>
        <span style={{ fontSize: "2rem" }}>📄</span>
        <span>No paragraphs yet</span>
        <span style={{ fontSize: "0.78rem" }}>
          Write or paste a paragraph on the left and click <strong>Add</strong>
        </span>
      </EmptyState>
    );
  }

  return (
    <ListWrap>
      {paragraphs.map((item, i) => (
        <ParagraphItem
          key={i}
          item={item}
          index={i}
          total={paragraphs.length}
          onDelete={onDelete}
          onMove={handleMove}
        />
      ))}
    </ListWrap>
  );
};

export default ParagraphList;
